import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  salesPointAPI,
  CreateSalesPointData,
  UpdateSalesPointData,
} from "../../services/salesPointAPI";
import { branchAPI } from "../../services/branchAPI";
import { useRBAC } from "../../hooks/useRBAC";
import { WORK_CATEGORY_OPTIONS, normalizeWorkCategory } from "../../constants/workCategories";

interface FormData {
  name: string;
  description: string;
  address: string;
  gstin: string;
  licence: string;
  phoneNo: string;
  message: string;
  taxMode: string;
  workstationType: string;
  branchId: string;
}

const TAX_MODE_OPTIONS = [
  { value: "CGST_SGST", label: "CGST + SGST" },
  { value: "IGST", label: "IGST" },
] as const;

const emptyForm: FormData = {
  name: "",
  description: "",
  address: "",
  gstin: "",
  licence: "",
  phoneNo: "",
  message: "",
  taxMode: "CGST_SGST",
  workstationType: "restaurant",
  branchId: "",
};

const optionalOrNull = (value: string) => {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

const SalesPointFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canUpdate } = useRBAC();
  const isCreateMode = location.pathname.endsWith("/new");
  const isEditMode = Boolean(id) && location.pathname.endsWith("/edit");
  const isViewMode = Boolean(id) && !isCreateMode && !isEditMode;
  const readOnly = isViewMode || (isEditMode && !canUpdate("sales_points"));

  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [apiValidationErrors, setApiValidationErrors] = useState<
    Record<string, string>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const parseMutationError = (error: any) => {
    const data = error?.response?.data;
    const validation = data?.validation as Record<string, string> | undefined;
    if (validation && Object.keys(validation).length > 0) {
      setApiValidationErrors(validation);
      setSaveError(data?.message || "Validation failed");
      return;
    }
    setApiValidationErrors({});
    setSaveError(
      data?.message || data?.error || error?.message || "Request failed"
    );
  };

  const {
    data: branches = [],
    isLoading: branchesLoading,
    error: branchesError,
  } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const {
    data: salesPointData,
    isLoading: salesPointLoading,
    error: salesPointError,
  } = useQuery({
    queryKey: ["salesPoint", id],
    queryFn: () => salesPointAPI.getSalesPointById(id!),
    enabled: Boolean(id) && (isEditMode || isViewMode),
  });

  const createSalesPointMutation = useMutation({
    mutationFn: salesPointAPI.createSalesPoint,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales_points"] });
      navigate("/sales-points");
    },
    onError: (error: any) => {
      parseMutationError(error);
    },
  });

  const updateSalesPointMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSalesPointData }) =>
      salesPointAPI.updateSalesPoint(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales_points"] });
      navigate("/sales-points");
    },
    onError: (error: any) => {
      parseMutationError(error);
    },
  });

  const isLoading =
    branchesLoading || ((isEditMode || isViewMode) && salesPointLoading);
  const error = branchesError || salesPointError;

  useEffect(() => {
    if (salesPointData) {
      setFormData({
        name: salesPointData.name || "",
        description: salesPointData.description || "",
        address: salesPointData.address || "",
        gstin: salesPointData.gstin || "",
        licence: salesPointData.licence || "",
        phoneNo: salesPointData.phoneNo || "",
        message: salesPointData.message || "",
        taxMode: salesPointData.taxMode || "CGST_SGST",
        workstationType: normalizeWorkCategory(
          salesPointData.workstationType || "restaurant"
        ),
        branchId: salesPointData.branchId || "",
      });
    }
  }, [salesPointData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    setApiValidationErrors({});
    const nextFieldErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      nextFieldErrors.name = "Sales point name is required";
    }
    if (!formData.branchId) {
      nextFieldErrors.branchId = "Branch selection is required";
    }
    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }
    setFieldErrors({});

    const payload = {
      name: formData.name.trim(),
      description: optionalOrNull(formData.description),
      address: optionalOrNull(formData.address),
      gstin: optionalOrNull(formData.gstin),
      licence: optionalOrNull(formData.licence),
      phoneNo: optionalOrNull(formData.phoneNo),
      message: optionalOrNull(formData.message),
      taxMode: formData.taxMode || "CGST_SGST",
      workstationType: formData.workstationType || "restaurant",
      branchId: formData.branchId,
    };

    if (isEditMode) {
      updateSalesPointMutation.mutate({ id: id!, data: payload });
    } else {
      createSalesPointMutation.mutate(payload as CreateSalesPointData);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  const fieldError = (key: string) =>
    fieldErrors[key] || apiValidationErrors[key] || "";

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/sales-points")}
          sx={{ mr: 2 }}
        >
          Back to Sales Points
        </Button>
        <Typography variant="h4" component="h1" sx={{ flex: 1 }}>
          {isViewMode
            ? "View Sales Point"
            : isEditMode
              ? "Edit sales point"
              : "Add New Sales Point"}
        </Typography>
        {isViewMode && canUpdate("sales_points") && id && (
          <Button
            variant="contained"
            onClick={() => navigate(`/sales-points/${id}/edit`)}
          >
            Edit
          </Button>
        )}
      </Box>

      <Paper elevation={2}>
        <Box p={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error instanceof Error ? error.message : "Failed to load data"}
            </Alert>
          )}

          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
              {saveError}
              {Object.keys(apiValidationErrors).length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {Object.entries(apiValidationErrors).map(([field, msg]) => (
                    <li key={field}>
                      <Typography variant="body2">
                        {field}: {msg}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required={!readOnly}
                  disabled={readOnly}
                  error={Boolean(fieldError("name"))}
                  helperText={fieldError("name") || "Unique sales point name"}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  required={!readOnly}
                  disabled={readOnly}
                  error={Boolean(fieldError("branchId"))}
                >
                  <InputLabel>Branch</InputLabel>
                  <Select
                    name="branchId"
                    value={formData.branchId}
                    onChange={handleSelectChange}
                    label="Branch"
                    disabled={readOnly}
                  >
                    <MenuItem value="">
                      <em>Select a branch</em>
                    </MenuItem>
                    {branches.map((branch) => (
                      <MenuItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldError("branchId") && (
                    <FormHelperText error>{fieldError("branchId")}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone No"
                  name="phoneNo"
                  value={formData.phoneNo}
                  onChange={handleChange}
                  disabled={readOnly}
                  error={Boolean(fieldError("phoneNo"))}
                  helperText={fieldError("phoneNo")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GSTIN"
                  name="gstin"
                  value={formData.gstin}
                  onChange={handleChange}
                  disabled={readOnly}
                  error={Boolean(fieldError("gstin"))}
                  helperText={fieldError("gstin")}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Licence"
                  name="licence"
                  value={formData.licence}
                  onChange={handleChange}
                  disabled={readOnly}
                  error={Boolean(fieldError("licence"))}
                  helperText={
                    fieldError("licence") ||
                    "Any licence string (FSSAI, drug, trade, etc.)"
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={Boolean(fieldError("taxMode"))}>
                  <InputLabel>Tax Mode</InputLabel>
                  <Select
                    name="taxMode"
                    value={formData.taxMode}
                    onChange={handleSelectChange}
                    label="Tax Mode"
                    disabled={readOnly}
                  >
                    {TAX_MODE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {fieldError("taxMode") ||
                      "CGST+SGST for same-state style; IGST for inter-state style"}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  error={Boolean(fieldError("workstationType"))}
                >
                  <InputLabel>Work category</InputLabel>
                  <Select
                    name="workstationType"
                    value={formData.workstationType}
                    onChange={handleSelectChange}
                    label="Work category"
                    disabled={readOnly}
                  >
                    {WORK_CATEGORY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {fieldError("workstationType") ||
                      "Restaurant: tables/KOT. Simple: grocery, medical, shop, wholesale billing. Items stay on the sales point."}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  disabled={readOnly}
                  error={Boolean(fieldError("address"))}
                  helperText={fieldError("address")}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  disabled={readOnly}
                  error={Boolean(fieldError("message"))}
                  helperText={
                    fieldError("message") ||
                    "Shown on receipts / invoices (optional)"
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description (optional)"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  disabled={readOnly}
                  error={Boolean(fieldError("description"))}
                  helperText={fieldError("description")}
                />
              </Grid>

              {!readOnly && (
                <Grid item xs={12}>
                  <Box display="flex" gap={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      disabled={
                        createSalesPointMutation.isPending ||
                        updateSalesPointMutation.isPending
                      }
                    >
                      {createSalesPointMutation.isPending ||
                      updateSalesPointMutation.isPending
                        ? "Saving..."
                        : isEditMode
                          ? "Update Sales Point"
                          : "Create sales point"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => navigate("/sales-points")}
                      disabled={
                        createSalesPointMutation.isPending ||
                        updateSalesPointMutation.isPending
                      }
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default SalesPointFormPage;
